import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, PlaceholderTag } from "../components/common";
import { PROJECTS } from "../data/site";

export default function OurWork() {
  const categories = ["All", ...new Set(PROJECTS.map((p) => p.category))];
  const years = ["All", ...new Set(PROJECTS.map((p) => p.sortKey.slice(0, 4)))];
  const [filter, setFilter] = useState("All");
  const [year, setYear] = useState("All");
  const visible = PROJECTS.filter(
    (p) => (filter === "All" || p.category === filter) && (year === "All" || p.sortKey.startsWith(year))
  );

  const chip = (active) =>
    `rounded-full border px-5 py-2 text-sm font-medium transition-all duration-300 ${
      active
        ? "bg-charcoal text-warmwhite border-charcoal"
        : "border-charcoal/20 text-charcoal/70 hover:border-charcoal hover:text-charcoal"
    }`;

  return (
    <>
      <PageHero
        overline="Our Work"
        title="Programmes & Projects"
        copy="Every entry in this archive is a documented event from the Trust's own reports, with real photography and locations. New programme areas and project details will be added as the Trust supplies them."
      />
      <section className="pb-24 md:pb-32">
        <div className="max-w-[90rem] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3" role="group" aria-label="Filter projects by category">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(c)}
                aria-pressed={filter === c}
                className={chip(filter === c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filter projects by year">
            <span className="text-xs font-mono uppercase tracking-widest text-charcoal/40 mr-2">Year</span>
            {years.map((y) => (
              <button
                key={y}
                data-testid={`filter-year-${y.toLowerCase()}`}
                onClick={() => setYear(y)}
                aria-pressed={year === y}
                className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-all duration-300 ${
                  year === y
                    ? "bg-terracotta text-white border-terracotta"
                    : "border-charcoal/15 text-charcoal/60 hover:border-charcoal hover:text-charcoal"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
          <p className="mt-6 text-xs font-mono uppercase tracking-widest text-charcoal/40" data-testid="project-count">
            Showing {visible.length} of {PROJECTS.length} documented events
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 2) * 0.08}>
                <article data-testid={`project-card-${p.slug}`} className="group">
                  <div className="aspect-[16/9] overflow-hidden rounded-2xl bg-softgray">
                    <img
                      src={p.image.url}
                      alt={p.image.alt}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    {p.real ? (
                      <span className="inline-flex w-fit items-center px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest bg-terracotta/10 text-terracotta">
                        Documented event
                      </span>
                    ) : (
                      <PlaceholderTag text="Temporary placeholder image" />
                    )}
                    <span className="text-xs font-mono uppercase tracking-widest text-terracotta">{p.category}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl font-medium tracking-tight">
                    {p.title ?? "Project title to be provided"}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/60">
                    {p.description ??
                      "A full project profile — description, location, dates, photo gallery, impact and related stories — will be published here once official documentation is supplied."}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-widest text-charcoal/45">
                    <span>{p.location ?? "Location TBC"}</span>
                    <span className="h-1 w-1 rounded-full bg-charcoal/30" />
                    <span>{p.date ?? "Date TBC"}</span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-16 max-w-2xl text-sm leading-relaxed text-charcoal/50 border-l-2 border-terracotta/50 pl-5">
              Dates and expanded project profiles — photo galleries, videos and related stories — will be added as
              the Trust confirms them. Further programme areas will appear here once officially documented.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
